import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useOutletContext } from "react-router-dom";
import { useAddStatus } from "api/tes/useAddStatus";
import { createVerificationStatus } from "lib/verification-utils";
import { useTakebackById } from "api/use-takebacks";
import { getQueryParams } from "lib/utils";
import LoadingSpinner from "components/loading-spinner";

export default function DropoffSellerCompletion() {
  const location = useLocation();
  const queryParams = getQueryParams(location.search);
  const { setSidebarVisual } = useOutletContext();
  const { mutateAsync: addStatus } = useAddStatus();
  const hasProcessedRef = useRef(false);
  const [isProcessing, setIsProcessing] = useState(true);

  // Fetch the specific takeback by ID from query params
  const takeback = useTakebackById({ id: queryParams.id });

  // Set sidebar visual
  useEffect(() => {
    setSidebarVisual("dollar");
  }, [setSidebarVisual]);

  // Wrap the async function in useCallback to make it stable
  const updateTakebackData = useCallback(async () => {
    if (!takeback || hasProcessedRef.current) return;

    setIsProcessing(true);
    try {
      // Check if takeback already has "processed" status to prevent duplicate processing
      const isAlreadyProcessed =
        takeback.rawTakeback?.takeback_processing_history_list?.some(
          (status) => status.status === "processed"
        );

      if (isAlreadyProcessed) {
        console.warn("Takeback already processed, skipping...");
        hasProcessedRef.current = true;
        return;
      }

      const verificationStatus = createVerificationStatus("seller-in-store");

      await addStatus({
        id: takeback.sys_id,
        input: verificationStatus,
      });

      // Mark as completed to prevent duplicate processing
      hasProcessedRef.current = true;
    } catch (error) {
      console.error("Failed to update takeback:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
      });
    } finally {
      setIsProcessing(false);
    }
  }, [takeback, addStatus]);

  // Update takeback when takeback is loaded
  useEffect(() => {
    updateTakebackData();
  }, [updateTakebackData]);

  if (isProcessing) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner isLoading={true} />
      </div>
    );
  }

  return (
    <div className="flex h-full items-center text-center gap-x-large">
      {/* Success message */}
      <div className="bg-white bg-opacity-90 p-6 md:p-8 md:top-8 w-full">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">Thanks!</h1>
        <p className="text-lg md:text-xl text-[#757575]">
          You're all set! You can close this window and finish your trade-in at
          the store.
        </p>
        <p
          className={`pt-[24px] ${!takeback?.number && "opacity-0"} transition-opacity duration-200`}
        >
          <span className="font-semibold">Trade-in ID:</span> {takeback?.number}
        </p>
      </div>
    </div>
  );
}
